import { ScaleElement } from '@scale/base-element';
import { shake } from '@scale/utilities';

const SUCCESS_PARAM = 'customer_posted';

class ScaleNewsletter extends ScaleElement {
  static requiredRefs = ['feedback'];

  #pending = false;

  setup() {
    const form = this.querySelector('form');
    form.noValidate = true;

    this.on('submit', (event) => this.#subscribe(event));
    this.on('input', (event) => {
      const input = event.target;
      if (input.getAttribute('aria-invalid') === 'true' && !this.#emailProblem(input)) this.#clearEmailError(input);
    }, { target: form });
  }

  async #subscribe(event) {
    event.preventDefault();
    if (this.#pending) return;

    const form = event.target;
    const email = form.elements['contact[email]'];
    const problem = this.#emailProblem(email);
    if (problem) {
      this.#rejectEmail(email, problem);
      return;
    }

    const submit = form.querySelector('[type="submit"]');

    this.#pending = true;
    submit.setAttribute('aria-busy', 'true');
    form.setAttribute('aria-busy', 'true');

    let error;
    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'text/html' },
      });
      const body = await response.text();

      if (new URL(response.url).searchParams.get(SUCCESS_PARAM) === 'true') {
        await this.#succeed(form);
      } else {
        error = this.#readError(body);
      }
    } catch {
      error = this.dataset.networkError;
    }

    if (error) {
      this.#refuse(email, error);
      await this.#sendBack(submit);
    }
    this.#pending = false;
    submit.removeAttribute('aria-busy');
    form.removeAttribute('aria-busy');
  }

  #emailProblem(input) {
    const value = input.value.trim();
    if (!value) return this.dataset.emailRequired;
    if (!value.includes('@')) return this.dataset.emailMissingAt;
    if (input.validity.typeMismatch || !/@[^@\s]+\.[^@\s]{2,}$/.test(value)) return this.dataset.emailIncomplete;
    return '';
  }

  #rejectEmail(input, message) {
    input.setAttribute('aria-invalid', 'true');
    input.focus();
    this.#refuse(input, message);
  }

  #refuse(input, message) {
    this.#showFeedback(message, 'error');
    shake(input.parentElement);
    if (this.#moves()) this.ref('feedback').animate([{ opacity: 0, translate: '0 -4px' }, { opacity: 1, translate: '0 0' }], this.#timing());
  }

  async #sendBack(button) {
    const shift = (y) => (this.#moves() ? `0 ${y}px` : '0 0');
    const timing = { ...this.#timing(), fill: 'forwards' };
    const label = [...button.children].map((child) => child.animate([{ opacity: 0, translate: shift(-8) }, { opacity: 1, translate: '0 0' }], timing));
    await button.animate([{ opacity: 1, translate: '0 0' }, { opacity: 0, translate: shift(8) }], { ...timing, pseudoElement: '::after' }).finished;
    button.removeAttribute('aria-busy');
    for (const animation of label) animation.cancel();
  }

  #moves() {
    return matchMedia('(prefers-reduced-motion: no-preference)').matches;
  }

  #timing() {
    const motion = getComputedStyle(this);
    return {
      duration: parseFloat(motion.getPropertyValue('--duration-normal')),
      easing: motion.getPropertyValue('--ease-out'),
    };
  }

  #clearEmailError(input) {
    input.removeAttribute('aria-invalid');
    const feedback = this.ref('feedback');
    feedback.hidden = true;
    feedback.textContent = '';
  }

  async #succeed(form) {
    const message = this.dataset.successMessage;
    const email = form.elements['contact[email]'].value;

    if (this.#moves()) {
      form.style.overflow = 'clip';
      await form.animate([{ height: `${form.offsetHeight}px`, opacity: 1 }, { height: '0px', opacity: 0 }], this.#timing()).finished;
      form.style.removeProperty('overflow');
    }
    form.hidden = true;
    this.#showFeedback(message, 'success');
    if (this.#moves()) this.ref('feedback').animate([{ opacity: 0, translate: '0 8px' }, { opacity: 1, translate: '0 0' }], this.#timing());
    this.emit('scale:newsletter:subscribed', { email });
  }

  #showFeedback(message, state) {
    const feedback = this.ref('feedback');
    feedback.classList.toggle('block-newsletter__feedback--success', state === 'success');
    feedback.classList.toggle('block-newsletter__feedback--error', state === 'error');
    feedback.hidden = false;
    feedback.textContent = message;
  }

  #readError(body) {
    const rendered = new DOMParser().parseFromString(body, 'text/html');
    const error = rendered.querySelector('.block-newsletter__feedback--error');
    const message = error?.textContent.trim();
    return message || this.dataset.genericError;
  }
}

if (!customElements.get('scale-newsletter')) {
  customElements.define('scale-newsletter', ScaleNewsletter);
}
